import React, { useState, useEffect, useMemo } from 'react';
import './BienestarPage.css';
import Header from './Header';
import AnimatedBackground from './AnimatedBackground';
import StoreCarousel from './StoreCarousel';

// Enlace de Google Meet para las clases de yoga (jueves 6am). Configura en .env como REACT_APP_BIENESTAR_MEET_LINK para sobrescribir
const MEET_LINK = process.env.REACT_APP_BIENESTAR_MEET_LINK || 'https://meet.google.com/pen-qhre-mkq';
const API_BASE = process.env.REACT_APP_API_URL || '/api';

// El link solo se muestra 10 min antes (5:50 AM) y 5 min después (7:05 AM) de la clase - hora Colombia, jueves
const isMeetLinkActive = () => {
  const now = new Date();
  const parts = new Intl.DateTimeFormat('en-CA', {
    timeZone: 'America/Bogota',
    hour: 'numeric',
    minute: 'numeric',
    weekday: 'short'
  }).formatToParts(now);
  const hour = parseInt(parts.find(p => p.type === 'hour').value, 10);
  const minute = parseInt(parts.find(p => p.type === 'minute').value, 10);
  const weekday = parts.find(p => p.type === 'weekday').value;
  if (weekday !== 'Thu') return false;
  const totalMin = hour * 60 + minute;
  return totalMin >= 5 * 60 + 50 && totalMin <= 7 * 60 + 5; // 5:50 AM - 7:05 AM
};

const BienestarPage = () => {
  const [selectedEvent, setSelectedEvent] = useState(null);
  const [showRegistration, setShowRegistration] = useState(false);
  const [registrationSuccess, setRegistrationSuccess] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [submitError, setSubmitError] = useState(null);
  const [selectedPaymentType, setSelectedPaymentType] = useState('single');
  const [currentMonth, setCurrentMonth] = useState(new Date().getMonth());
  const [currentYear, setCurrentYear] = useState(new Date().getFullYear());
  const [activityFilter, setActivityFilter] = useState('all');

  // Scroll al top cuando se monta el componente
  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);

  const [instructorMode, setInstructorMode] = useState(false);
  const [ticketEmail, setTicketEmail] = useState('');
  const [ticketResult, setTicketResult] = useState(null);
  const [consuming, setConsuming] = useState(false);
  const [consumeKey, setConsumeKey] = useState('');
  const [formLoadTime, setFormLoadTime] = useState(null);

  // Formulario de interés en cursos futuros (dentro del modal)
  const [interestEmail, setInterestEmail] = useState('');
  const [interestCourse, setInterestCourse] = useState('');
  const [interestSubmitting, setInterestSubmitting] = useState(false);
  const [interestSuccess, setInterestSuccess] = useState(false);
  const [interestError, setInterestError] = useState(null);
  const [selectedJavilandCourse, setSelectedJavilandCourse] = useState(null);

  // Cursos informativos de Javiland (Próximos Eventos de Javiland)
  const javilandCourses = [
    {
      id: 'equilibrio',
      title: 'Dominando el Equilibrio',
      badge: '5 clases',
      shortDesc: 'La quietud en el movimiento. La estabilidad nace de la atención plena.',
      fullDesc: 'Serie de 5 clases enfocadas en equilibrios de pie, transiciones dinámicas y preparación para equilibrios de brazos (Bakasana/Cuervo). Incluye: enraizamiento y Drishti, equilibrio sobre una pierna, equilibrio dinámico y transiciones, introducción a Bakasana, integración y equilibrios avanzados.',
      philosophy: 'La quietud en el movimiento. La estabilidad nace de la atención plena.'
    },
    {
      id: 'inversiones',
      title: 'Camino a las Inversiones',
      badge: '7 clases',
      shortDesc: 'La inversión es una metáfora de ver el mundo desde otra perspectiva.',
      fullDesc: 'Serie de 7 clases para una preparación segura hacia las inversiones. Incluye: cimientos de cintura escapular y muñecas, núcleo de poder (core), Viparita Karani (piernas en la pared), preparación para Sirsasana (parada de cabeza), práctica de Sirsasana con pared, preparación para parada de manos, integración y secuencia de inversiones.',
      philosophy: 'La inversión es una metáfora de ver el mundo desde otra perspectiva. La seguridad es la prioridad absoluta.'
    }
  ];

  // Mostrar mensaje al volver de MercadoPago
  useEffect(() => {
    const params = new URLSearchParams(window.location.search);
    setInstructorMode(params.get('instructor') === '1');
    const payment = params.get('payment');
    if (payment === 'success') {
      setShowRegistration(true);
      setRegistrationSuccess(true);
      window.history.replaceState({}, '', '/bienestar');
    } else if (payment === 'failure' || payment === 'pending') {
      setSubmitError(payment === 'failure' ? 'El pago no se completó. Intenta de nuevo.' : 'Pago pendiente. Te notificaremos cuando se confirme.');
      setShowRegistration(true);
      window.history.replaceState({}, '', '/bienestar');
    }
  }, []);

  // Configuración del carrusel de bienestar - imágenes IA (estilo linocut)
  const carouselSlides = [
    {
      type: 'image',
      src: `${process.env.PUBLIC_URL || ''}/images/bienestar-carousel-yoga-meditacion.png`,
      fallbackSrc: `${process.env.PUBLIC_URL || ''}/images/savila.png`,
      subtitle: 'Transforma tu vida',
      title: 'Yoga & Meditación',
      description: 'Sesiones personalizadas para tu bienestar físico y mental',
      cta: {
        text: 'Ver Calendario',
        onClick: () => {
          const calendar = document.querySelector('.calendar-section');
          if (calendar) calendar.scrollIntoView({ behavior: 'smooth' });
        }
      }
    },
    {
      type: 'image',
      src: `${process.env.PUBLIC_URL || ''}/images/bienestar-carousel-talleres-crecimiento.png`,
      fallbackSrc: `${process.env.PUBLIC_URL || ''}/images/montana.png`,
      subtitle: 'Experiencias únicas',
      title: 'Talleres de Crecimiento',
      description: 'Aprende técnicas de alimentación consciente y desarrollo personal',
      cta: {
        text: 'Explorar Talleres',
        onClick: () => {
          const calendar = document.querySelector('.calendar-section');
          if (calendar) calendar.scrollIntoView({ behavior: 'smooth' });
        }
      }
    },
    {
      type: 'image',
      src: `${process.env.PUBLIC_URL || ''}/images/bienestar-carousel-conecta-esencia.png`,
      fallbackSrc: `${process.env.PUBLIC_URL || ''}/images/HojaCannabis.png`,
      subtitle: 'Comunidad consciente',
      title: 'Conecta con tu Esencia',
      description: 'Únete a nuestra comunidad de crecimiento y transformación',
      cta: {
        text: 'Unirse Ahora',
        onClick: () => {
          const calendar = document.querySelector('.calendar-section');
          if (calendar) calendar.scrollIntoView({ behavior: 'smooth' });
        }
      }
    },
  ];

  const allEvents = useMemo(() => {
    const evs = [];
    let id = 1;

    const thursdaysByMonth = [
      [26],
      [5, 12, 19, 26],
      [2, 9, 16, 23, 30],
      [7, 14, 21, 28]
    ];
    const yogaJavilandBase = {
      title: 'Clase de Yoga Javiland',
      dateRange: 'Todos los jueves',
      time: '6:00 AM',
      duration: '60 min',
      instructor: 'Javiland',
      instructorEmail: 'javi@javiland.com',
      description: '60 min semanales para conectar contigo mismo. Yoga, respiración y un espacio para practicar en comunidad. Clase online por Google Meet. Gratis. Programa de iniciación.',
      maxParticipants: 20,
      category: 'Yoga',
      isRecurring: true,
      location: 'Online · Google Meet'
    };

    const trainingSaturdaysByMonth = [
      [7, 14, 21],
      [7, 14, 21, 28],
      [4, 11, 18, 25],
      []
    ];
    const trainingBase = {
      title: 'Entrenamiento deportivo consciente',
      dateRange: 'Sábados en agenda',
      time: '8:00 AM',
      duration: '50 min',
      instructor: 'Coordinación Bienestar Javiland',
      instructorEmail: 'javi@javiland.com',
      description: 'Movimiento funcional, fuerza amable y hábitos atléticos conscientes. Pensado para complementar tu yoga o iniciar una rutina guiada. Modalidad y lugar se confirman al reservar.',
      maxParticipants: 12,
      category: 'Entrenamiento',
      isRecurring: true,
      location: 'Presencial u online — según cohorte'
    };

    [1, 2, 3, 4].forEach((m, mi) => {
      thursdaysByMonth[mi].forEach(d => {
        evs.push({
          ...yogaJavilandBase,
          id: `yoga-jue-${id++}`,
          date: new Date(currentYear, m, d),
          price: 0,
          status: 'Gratuita'
        });
      });
      (trainingSaturdaysByMonth[mi] || []).forEach(d => {
        evs.push({
          ...trainingBase,
          id: `training-sat-${id++}`,
          date: new Date(currentYear, m, d),
          price: 0,
          status: 'Cupos limitados'
        });
      });
    });

    const cutoff = new Date(currentYear, 3, 1);
    return evs.filter(e => e.date < cutoff).sort((a, b) => a.date - b.date);
  }, [currentYear]);

  const events = useMemo(() => {
    if (activityFilter === 'yoga') return allEvents.filter(e => e.category === 'Yoga');
    if (activityFilter === 'training') return allEvents.filter(e => e.category === 'Entrenamiento');
    return allEvents;
  }, [allEvents, activityFilter]);

  // Para el banner y modal - evento base de yoga (siempre gratuita, programa de iniciación)
  const freeYogaEvent = {
    id: 'yoga-javiland',
    title: 'Clase de Yoga Javiland',
    date: allEvents.find(e => e.category === 'Yoga' && e.status === 'Gratuita')?.date || new Date(currentYear, 1, 26),
    dateRange: 'Todos los jueves',
    time: '6:00 AM',
    duration: '60 min',
    instructor: 'Javiland',
    instructorEmail: 'javi@javiland.com',
    description: '60 min semanales para conectar contigo mismo. Yoga, respiración y un espacio para practicar en comunidad. Clase online por Google Meet. Gratis. Programa de iniciación.',
    price: 0,
    maxParticipants: 20,
    category: 'Yoga',
    status: 'Gratuita',
    isRecurring: true,
    location: 'Online · Google Meet'
  };

  const months = [
    'Enero', 'Febrero', 'Marzo', 'Abril', 'Mayo', 'Junio',
    'Julio', 'Agosto', 'Septiembre', 'Octubre', 'Noviembre', 'Diciembre'
  ];

  const getDaysInMonth = (month, year) => {
    return new Date(year, month + 1, 0).getDate();
  };

  const getFirstDayOfMonth = (month, year) => {
    return new Date(year, month, 1).getDay();
  };

  const getEventsForDay = (day) => {
    return events.filter(event => 
      event.date.getDate() === day && 
      event.date.getMonth() === currentMonth && 
      event.date.getFullYear() === currentYear
    );
  };

  const handleEventClick = (event) => {
    setSelectedEvent(event);
  };

  const handleCheckTickets = async () => {
    if (!ticketEmail.trim()) return;
    setTicketResult(null);
    try {
      const res = await fetch(`${API_BASE}/bienestar/tickets?email=${encodeURIComponent(ticketEmail.trim())}`);
      const data = await res.json();
      setTicketResult(data);
    } catch {
      setTicketResult({ success: false, message: 'Error de conexión' });
    }
  };

  const handleConsumeSession = async () => {
    if (!ticketEmail.trim()) return;
    setConsuming(true);
    try {
      const body = { email: ticketEmail.trim() };
      if (consumeKey.trim()) body.key = consumeKey.trim();
      const res = await fetch(`${API_BASE}/bienestar/consume`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(body)
      });
      const data = await res.json();
      setTicketResult(data);
      if (data.success) handleCheckTickets();
    } catch {
      setTicketResult({ success: false, message: 'Error de conexión' });
    } finally {
      setConsuming(false);
    }
  };

  const handleRegistration = () => {
    setShowRegistration(true);
    setSubmitError(null);
    setSelectedPaymentType('single'); // Por defecto 1 clase
    setFormLoadTime(Math.floor(Date.now() / 1000)); // Anti-spam: tiempo mínimo
  };

  const handleFormSubmit = async (e) => {
    e.preventDefault();
    const form = e.target;
    const formData = new FormData(form);
    const paymentType = form.querySelector('input[name="paymentType"]:checked')?.value || 'single';
    const price = selectedEvent?.price || 0;
    const ticketPrice = selectedEvent?.ticketPrice || 0;

    setIsSubmitting(true);
    setSubmitError(null);

    try {
      const honeypot = formData.get('website')?.trim() || '';
      const payload = {
        name: formData.get('name')?.trim() || '',
        email: formData.get('email')?.trim() || '',
        phone: formData.get('phone')?.trim() || '',
        comments: formData.get('comments')?.trim() || '',
        eventTitle: selectedEvent?.title || '',
        eventDate: selectedEvent?.date?.toLocaleDateString('es-ES') || selectedEvent?.dateRange || '',
        eventTime: selectedEvent?.time || '',
        eventInstructorEmail: selectedEvent?.instructorEmail || '',
        price,
        paymentType,
        _honeypot: honeypot,
        _formLoadTime: formLoadTime
      };
      if (paymentType === 'ticket' && ticketPrice > 0) {
        payload.ticketPrice = ticketPrice;
      }

      const res = await fetch(`${API_BASE}/bienestar/register`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(payload)
      });
      const data = await res.json();

      if (res.ok && data.success) {
        if (data.requiresPayment && data.paymentUrl) {
          window.location.href = data.paymentUrl;
          return;
        }
        setRegistrationSuccess(true);
      } else {
        setSubmitError(data.message || 'Error al enviar. Intenta de nuevo.');
      }
    } catch (err) {
      setSubmitError('Error de conexión. Intenta de nuevo.');
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleJavilandCourseClick = (course) => {
    setSelectedJavilandCourse(course);
    setInterestCourse(course.id);
    setInterestEmail('');
    setInterestSuccess(false);
    setInterestError(null);
  };

  const handleInterestSubmit = async (e) => {
    e.preventDefault();
    if (!interestEmail.trim() || !interestCourse) return;
    setInterestSubmitting(true);
    setInterestError(null);
    try {
      const res = await fetch(`${API_BASE}/bienestar/interest`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ email: interestEmail.trim(), course: interestCourse })
      });
      const data = await res.json();
      if (res.ok && data.success) {
        setInterestSuccess(true);
        setInterestEmail('');
      } else {
        setInterestError(data.message || 'Error al enviar. Intenta de nuevo.');
      }
    } catch (err) {
      setInterestError('Error de conexión. Intenta de nuevo.');
    } finally {
      setInterestSubmitting(false);
    }
  };

  const renderCalendar = () => {
    const daysInMonth = getDaysInMonth(currentMonth, currentYear);
    const firstDay = getFirstDayOfMonth(currentMonth, currentYear);
    const days = [];

    // Días vacíos al inicio del mes
    for (let i = 0; i < firstDay; i++) {
      days.push(<div key={`empty-${i}`} className="calendar-day empty"></div>);
    }

    // Días del mes
    for (let day = 1; day <= daysInMonth; day++) {
      const dayEvents = getEventsForDay(day);
      days.push(
        <div 
          key={day} 
          className={`calendar-day ${dayEvents.length > 0 ? 'has-events clickable' : ''}`}
          onClick={dayEvents.length > 0 ? () => handleEventClick(dayEvents[0]) : undefined}
          role={dayEvents.length > 0 ? 'button' : undefined}
          tabIndex={dayEvents.length > 0 ? 0 : undefined}
          onKeyDown={dayEvents.length > 0 ? (e) => e.key === 'Enter' && handleEventClick(dayEvents[0]) : undefined}
        >
          <span className="day-number">{day}</span>
          {dayEvents.length > 0 && (
            <div className="event-indicators">
              {dayEvents.slice(0, 2).map(event => (
                <div key={event.id} className="event-dot" title={event.title}></div>
              ))}
              {dayEvents.length > 2 && (
                <div className="more-events">+{dayEvents.length - 2}</div>
              )}
            </div>
          )}
        </div>
      );
    }

    return days;
  };

  const nextMonth = () => {
    if (currentMonth === 11) {
      setCurrentMonth(0);
      setCurrentYear(currentYear + 1);
    } else {
      setCurrentMonth(currentMonth + 1);
    }
  };

  const prevMonth = () => {
    if (currentMonth === 0) {
      setCurrentMonth(11);
      setCurrentYear(currentYear - 1);
    } else {
      setCurrentMonth(currentMonth - 1);
    }
  };

  return (
    <div className="bienestar-page">
      <Header />
      <AnimatedBackground particleCount={20} staticCount={6} />
      
      {/* Carousel de Bienestar */}
      <StoreCarousel slides={carouselSlides} autoplayInterval={7000} height="550px" />
      
      <main className="bienestar-main">
        <div className="container">

          {/* Clase Yoga Javiland - gratuita, programa de iniciación */}
          <div className="free-yoga-banner">
            <div className="free-yoga-content">
              <span className="free-yoga-badge">Gratuita</span>
              <h2 className="free-yoga-title">Clase de Yoga Javiland</h2>
              <p className="free-yoga-schedule">
                <i className="fas fa-calendar-week"></i> Todos los jueves · 6:00 AM (hora Colombia)
              </p>
              <p className="free-yoga-schedule free-yoga-note">
                Primera clase: 26 de febrero. Gratis. Programa de iniciación.
              </p>
              <p className="free-yoga-online">
                <i className="fas fa-video"></i> Online · Google Meet · 60 min
              </p>
            </div>
            <div className="free-yoga-right">
              <p className="free-yoga-desc">
                60 min semanales para conectar contigo mismo. Yoga, respiración y un espacio 
                para practicar en comunidad.
              </p>
              <button className="free-yoga-cta" onClick={() => handleEventClick(freeYogaEvent)}>
                <i className="fas fa-user-plus"></i>
                Reservar mi cupo
              </button>
            </div>
          </div>

          <div className="training-spot-banner">
            <div className="training-spot-inner">
              <span className="training-spot-badge">Nuevo en agenda</span>
              <h3 className="training-spot-title">Entrenamiento deportivo consciente</h3>
              <p className="training-spot-copy">
                Sábados en el calendario: fuerza modular, cardio amable y cuidado de articulaciones. Reserva plaza como el resto
                de sesiones desde la vista detalle.
              </p>
              <button type="button" className="training-spot-cta" onClick={() => setActivityFilter('training')}>
                Ver solo entrenamiento
              </button>
            </div>
          </div>

          <div className="activity-filter-toolbar">
            <span className="activity-filter-label">Filtrar calendario y lista:</span>
            <div className="activity-filter-buttons">
              <button
                type="button"
                className={`activity-filter-btn ${activityFilter === 'all' ? 'is-active' : ''}`}
                onClick={() => setActivityFilter('all')}
              >
                Todas
              </button>
              <button
                type="button"
                className={`activity-filter-btn ${activityFilter === 'yoga' ? 'is-active' : ''}`}
                onClick={() => setActivityFilter('yoga')}
              >
                Yoga
              </button>
              <button
                type="button"
                className={`activity-filter-btn ${activityFilter === 'training' ? 'is-active' : ''}`}
                onClick={() => setActivityFilter('training')}
              >
                Entrenamiento
              </button>
            </div>
          </div>

          <div className="calendar-section">
            <div className="calendar-header">
              <button className="calendar-nav-btn" onClick={prevMonth}>
                <i className="fas fa-chevron-left"></i>
              </button>
              <h2 className="calendar-title">
                {months[currentMonth]} {currentYear}
              </h2>
              <button className="calendar-nav-btn" onClick={nextMonth}>
                <i className="fas fa-chevron-right"></i>
              </button>
            </div>

            <div className="calendar-grid">
              <div className="calendar-weekdays">
                <div className="weekday">Dom</div>
                <div className="weekday">Lun</div>
                <div className="weekday">Mar</div>
                <div className="weekday">Mié</div>
                <div className="weekday">Jue</div>
                <div className="weekday">Vie</div>
                <div className="weekday">Sáb</div>
              </div>
              <div className="calendar-days">
                {renderCalendar()}
              </div>
            </div>
          </div>

          {instructorMode && (
            <div className="instructor-panel">
              <h3 className="section-title">Área instructor — Tiqueteras</h3>
              <div className="instructor-form">
                <input
                  type="email"
                  placeholder="Email del participante"
                  value={ticketEmail}
                  onChange={(e) => setTicketEmail(e.target.value)}
                />
                <input
                  type="password"
                  placeholder="Clave instructor (opcional)"
                  value={consumeKey}
                  onChange={(e) => setConsumeKey(e.target.value)}
                  title="Si el servidor exige clave, introdúcela aquí"
                />
                <button type="button" className="instructor-btn" onClick={handleCheckTickets}>
                  Consultar sesiones
                </button>
                <button type="button" className="instructor-btn consume" onClick={handleConsumeSession} disabled={consuming}>
                  {consuming ? '...' : 'Consumir 1 sesión'}
                </button>
              </div>
              {ticketResult && (
                <div className={`ticket-result ${ticketResult.success ? 'success' : 'error'}`}>
                  {ticketResult.success ? (
                    <>
                      {ticketResult.tickets?.length > 0 ? (
                        <div>
                          <p><strong>Sesiones disponibles:</strong> {ticketResult.totalRemaining}</p>
                          <ul>
                            {ticketResult.tickets.map((t, i) => (
                              <li key={i}>Tiquetera #{t.id} — {t.sessions_remaining} de {t.sessions_total} restantes</li>
                            ))}
                          </ul>
                        </div>
                      ) : (
                        <p>No tiene tiqueteras activas.</p>
                      )}
                      {ticketResult.message && <p>{ticketResult.message}</p>}
                    </>
                  ) : (
                    <p>{ticketResult.message || 'Error'}</p>
                  )}
                </div>
              )}
            </div>
          )}

          <div className="upcoming-events">
            <h3 className="section-title">Próximos Eventos cercanos</h3>
            <div className="events-grid">
              {events.map(event => (
                <div 
                  key={event.id} 
                  className="event-card"
                  onClick={() => handleEventClick(event)}
                >
                  <div className="event-date">
                    <span className="day">{event.date.getDate()}</span>
                    <span className="month">{months[event.date.getMonth()].substring(0, 3)}</span>
                  </div>
                  <div className="event-info">
                    <h4 className="event-title">{event.title}</h4>
                    <p className="event-time">
                      <i className="fas fa-clock"></i>
                      {event.time}{event.duration ? ` - ${event.duration}` : ''}
                    </p>
                    <p className="event-instructor">
                      <i className="fas fa-user"></i>
                      {event.instructor}
                    </p>
                    <div className="event-price">
                      {event.price > 0 ? `$${event.price.toLocaleString()} COP` : (event.status === 'Gratuita' ? 'Gratuita' : event.status || 'Consultar')}
                    </div>
                  </div>
                </div>
              ))}
            </div>
          </div>

          {/* Próximos Eventos de Javiland - Cursos informativos (click abre modal) */}
          <div className="javiland-events-section">
            <h3 className="section-title">Próximos Eventos de Javiland</h3>
            <p className="javiland-events-intro">
              Cursos que ofreceremos próximamente. Haz clic en cada curso para ver más información y registrar tu interés.
            </p>
            <div className="javiland-events-grid">
              {javilandCourses.map(course => (
                <div
                  key={course.id}
                  className="javiland-event-card"
                  onClick={() => handleJavilandCourseClick(course)}
                  role="button"
                  tabIndex={0}
                  onKeyDown={(e) => e.key === 'Enter' && handleJavilandCourseClick(course)}
                >
                  <span className="javiland-event-badge">{course.badge}</span>
                  <h4 className="javiland-event-title">{course.title}</h4>
                  <p className="javiland-event-desc">{course.shortDesc}</p>
                </div>
              ))}
            </div>
          </div>
        </div>
      </main>

      {/* Modal de detalles del evento */}
      {selectedEvent && (
        <div className="event-modal-overlay" onClick={() => setSelectedEvent(null)}>
          <div className="event-modal" onClick={e => e.stopPropagation()}>
            <button 
              className="modal-close"
              onClick={() => setSelectedEvent(null)}
            >
              <i className="fas fa-times"></i>
            </button>
            
            <div className="event-modal-content">
              <div className="event-modal-header">
                {selectedEvent.program && (
                  <span className="event-program-badge">{selectedEvent.program}</span>
                )}
                <h3 className="event-modal-title">{selectedEvent.title}</h3>
                {selectedEvent.status && (
                  <span className="event-status-badge">{selectedEvent.status}</span>
                )}
                <div className="event-modal-meta">
                  <span className="event-date">
                    <i className="fas fa-calendar"></i>
                    {selectedEvent.dateRange || selectedEvent.date.toLocaleDateString('es-ES', { 
                      weekday: 'long', 
                      year: 'numeric', 
                      month: 'long', 
                      day: 'numeric' 
                    })}
                  </span>
                  <span className="event-time">
                    <i className="fas fa-clock"></i>
                    {selectedEvent.time}{selectedEvent.duration ? ` - ${selectedEvent.duration}` : ''}
                  </span>
                </div>
              </div>

              <div className="event-modal-body">
                <div className="event-details">
                  {selectedEvent.location && (
                    <div className="detail-item">
                      <i className="fas fa-map-marker-alt"></i>
                      <span>{selectedEvent.location}</span>
                    </div>
                  )}
                  <div className="detail-item">
                    <i className="fas fa-user"></i>
                    <span>Organizador: {selectedEvent.instructor}</span>
                  </div>
                  {selectedEvent.maxParticipants > 0 && (
                    <div className="detail-item">
                      <i className="fas fa-users"></i>
                      <span>Máximo {selectedEvent.maxParticipants} participantes</span>
                    </div>
                  )}
                  <div className="detail-item">
                    <i className="fas fa-tag"></i>
                    <span>Categoría: {selectedEvent.category}</span>
                  </div>
                  {selectedEvent.program && (
                    <div className="detail-item">
                      <i className="fas fa-map-marked-alt"></i>
                      <span>Programa: {selectedEvent.program}</span>
                    </div>
                  )}
                </div>

                <div className="event-description">
                  <h4>Descripción</h4>
                  <p>{selectedEvent.description}</p>
                </div>

                {selectedEvent.ticketPrice && (
                  <div className="event-ticket-option">
                    <h4>Opción tiquetera</h4>
                    <p>{selectedEvent.ticketSessions} sesiones por ${selectedEvent.ticketPrice?.toLocaleString()} COP (10% descuento)</p>
                  </div>
                )}

                <div className="event-price-section">
                  <div className="price">
                    {selectedEvent.price > 0 ? (
                      <>
                        <span className="price-amount">${selectedEvent.price.toLocaleString()}</span>
                        <span className="price-currency">COP</span>
                      </>
                    ) : (
                      <span className="price-amount">{selectedEvent.status === 'Gratuita' ? 'Gratuita' : (selectedEvent.status || 'Consultar')}</span>
                    )}
                  </div>
                </div>

                <button 
                  className="register-btn"
                  onClick={handleRegistration}
                >
                  <i className="fas fa-user-plus"></i>
                  Registrarse
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Modal de registro */}
      {showRegistration && (
        <div className="registration-modal-overlay" onClick={() => setShowRegistration(false)}>
          <div className="registration-modal" onClick={e => e.stopPropagation()}>
            <button 
              className="modal-close"
              onClick={() => setShowRegistration(false)}
            >
              <i className="fas fa-times"></i>
            </button>
            
            <div className="registration-content">
              {registrationSuccess ? (
                <div className="registration-success">
                  <div className="success-icon"><i className="fas fa-check-circle"></i></div>
                  <h4>Reserva confirmada</h4>
                  <p>Te enviaremos el enlace de Google Meet al correo que registraste.</p>
                  {MEET_LINK && isMeetLinkActive() && (
                    <a href={MEET_LINK} target="_blank" rel="noopener noreferrer" className="meet-link-btn">
                      <i className="fas fa-external-link-alt"></i> Ir a la clase (Google Meet)
                    </a>
                  )}
                  <button type="button" className="pay-btn" onClick={() => { setShowRegistration(false); setRegistrationSuccess(false); }}>
                    Cerrar
                  </button>
                </div>
              ) : (
              <>
              <h3>Registro para {selectedEvent?.title}</h3>
              <p>Completa el formulario para reservar tu cupo</p>
              <p className="registration-online-note">
                <i className="fas fa-video"></i> Clase online por Google Meet
              </p>
              {submitError && (
                <div className="registration-error">
                  <i className="fas fa-exclamation-circle"></i> {submitError}
                </div>
              )}
              <form className="registration-form" onSubmit={handleFormSubmit}>
                {/* Honeypot: campo oculto para bots - no rellenar */}
                <div className="form-group honeypot-field" aria-hidden="true">
                  <label htmlFor="website">Sitio web (dejar vacío)</label>
                  <input type="text" id="website" name="website" tabIndex={-1} autoComplete="off" />
                </div>
                <div className="form-group">
                  <label>Nombre completo</label>
                  <input type="text" name="name" required />
                </div>
                <div className="form-group">
                  <label>Email</label>
                  <input type="email" name="email" required />
                </div>
                <div className="form-group">
                  <label>Teléfono</label>
                  <input type="tel" name="phone" required />
                </div>
                <div className="form-group">
                  <label>Comentarios adicionales</label>
                  <textarea name="comments" rows="3"></textarea>
                </div>
                
                {selectedEvent?.price > 0 && (
                  <div className="payment-summary">
                    <h4>Resumen del pago</h4>
                    {selectedEvent?.ticketPrice && (
                      <div className="payment-options">
                        <label className="payment-option">
                          <input
                            type="radio"
                            name="paymentType"
                            value="single"
                            checked={selectedPaymentType === 'single'}
                            onChange={() => setSelectedPaymentType('single')}
                          />
                          <span>1 clase — ${selectedEvent?.price?.toLocaleString()} COP</span>
                        </label>
                        <label className="payment-option">
                          <input
                            type="radio"
                            name="paymentType"
                            value="ticket"
                            checked={selectedPaymentType === 'ticket'}
                            onChange={() => setSelectedPaymentType('ticket')}
                          />
                          <span>Tiquetera {selectedEvent?.ticketSessions} sesiones — ${selectedEvent?.ticketPrice?.toLocaleString()} COP (10% dto.)</span>
                        </label>
                      </div>
                    )}
                    {!selectedEvent?.ticketPrice && (
                      <div className="payment-item">
                        <span>{selectedEvent?.title}</span>
                        <span>${selectedEvent?.price?.toLocaleString()} COP</span>
                      </div>
                    )}
                    <div className="payment-total">
                      <span>Total</span>
                      <span>${(selectedPaymentType === 'ticket' && selectedEvent?.ticketPrice
                        ? selectedEvent.ticketPrice
                        : selectedEvent?.price || 0
                      ).toLocaleString()} COP</span>
                    </div>
                  </div>
                )}

                <button type="submit" className="pay-btn" disabled={isSubmitting}>
                  <i className={selectedEvent?.price === 0 ? 'fas fa-user-plus' : 'fas fa-credit-card'}></i>
                  {isSubmitting ? 'Enviando...' : (selectedEvent?.price === 0 ? 'Reservar mi cupo' : 'Proceder al pago')}
                </button>
              </form>
              </>
              )}
            </div>
          </div>
        </div>
      )}

      {/* Modal curso Javiland (Próximos Eventos de Javiland) */}
      {selectedJavilandCourse && (
        <div className="event-modal-overlay" onClick={() => setSelectedJavilandCourse(null)}>
          <div className="event-modal javiland-course-modal" onClick={e => e.stopPropagation()}>
            <button 
              className="modal-close"
              onClick={() => setSelectedJavilandCourse(null)}
            >
              <i className="fas fa-times"></i>
            </button>
            
            <div className="event-modal-content">
              <div className="event-modal-header">
                <span className="event-program-badge">{selectedJavilandCourse.badge}</span>
                <h3 className="event-modal-title">{selectedJavilandCourse.title}</h3>
                <p className="javiland-course-philosophy">{selectedJavilandCourse.philosophy}</p>
              </div>

              <div className="event-modal-body">
                <div className="event-description">
                  <h4>Descripción</h4>
                  <p>{selectedJavilandCourse.fullDesc}</p>
                </div>

                <div className="javiland-interest-section">
                  <h4>Registra tu interés</h4>
                  <p className="javiland-interest-note">Te contactaremos cuando el curso esté disponible.</p>
                  {interestSuccess ? (
                    <div className="interest-success">
                      <i className="fas fa-check-circle"></i> Gracias. Te contactaremos cuando el curso esté disponible.
                    </div>
                  ) : (
                    <form className="interest-form" onSubmit={handleInterestSubmit}>
                      <div className="interest-form-row">
                        <input
                          type="email"
                          placeholder="Tu email"
                          value={interestEmail}
                          onChange={(e) => setInterestEmail(e.target.value)}
                          required
                        />
                        <button type="submit" className="interest-btn" disabled={interestSubmitting}>
                          {interestSubmitting ? 'Enviando...' : 'Enviar'}
                        </button>
                      </div>
                      {interestError && (
                        <div className="interest-error">
                          <i className="fas fa-exclamation-circle"></i> {interestError}
                        </div>
                      )}
                    </form>
                  )}
                </div>
              </div>
            </div>
          </div>
        </div>
      )}

    </div>
  );
};

export default BienestarPage;
